import Vuex from 'vuex';
import getters from './getters';
import mutations from './mutations';
import {Tag} from '@/profile-creation-wizard/components/for-creation/Tag';
import {ProfileFamily} from '@/common/api/api-raw-types';
import {FamiliesCategoriesEdition} from '@/categories/components/FamiliesCategories';

export type QrCodeSetup = {
    url: string | null;
};

export type ProfileCreationForm = {
    familiesCategoriesEdition: FamiliesCategoriesEdition;
    headline: string | null;
    experienceLevel: ExperienceLevel | null;
    tags: Tag[];
    functionalSkills: FunctionalSkill[];
    industryExpertises: IndustrySkill[];
    price: number;
    priceHide: boolean;
    address: Address;
    workPlacePreference: WorkplacePreference | null;
    languages: Language[];
    phone: string | null;
    invitationCode: string | null;
    groupInvitationId: string | null;
};

export const defaultAddress: Address = {
    city: null,
    countryCode: null,
    country: null,
    administrativeAreaLevel1: null,
    administrativeAreaLevel1Code: null,
    administrativeAreaLevel2: null,
    administrativeAreaLevel2Code: null,
    administrativeAreaLevel3: null,
    administrativeAreaLevel3Code: null,
    administrativeAreaLevel4: null,
    administrativeAreaLevel4Code: null,
    lon: null,
    lat: null,
    formattedAddress: null,
};

export enum ExperienceLevel {
    ENTRY = 'ENTRY',
    INTERMEDIATE = 'INTERMEDIATE',
    EXPERT = 'EXPERT',
    EXPERT_PLUS = 'EXPERT_PLUS',
}

export type Address = {
    city: string | null;
    countryCode: string | null;
    country: string | null;
    administrativeAreaLevel1: string | null;
    administrativeAreaLevel1Code: string | null;
    administrativeAreaLevel2: string | null;
    administrativeAreaLevel2Code: string | null;
    administrativeAreaLevel3: string | null;
    administrativeAreaLevel3Code: string | null;
    administrativeAreaLevel4: string | null;
    administrativeAreaLevel4Code: string | null;
    lon: number | null;
    lat: number | null;
    formattedAddress: string | null;
    [key: string]: any;
};

export enum WorkplacePreference {
    ONSITE = 'ONSITE',
    REMOTE = 'REMOTE',
}

export type Language = {
    id: number;
    code: string;
    level: LanguageLevel | null;
    query: string | null;
};

export enum LanguageLevel {
    BASIC = 'BASIC',
    CONVERSATIONAL = 'CONVERSATIONAL',
    FLUENT = 'FLUENT',
    NATIVE = 'NATIVE',
}

export type SearchableLanguage = {
    code: string;
    name: string;
};

export type FunctionalScope = {
    label: string;
    functionalSkills: FunctionalSkill[];
};

export type FunctionalSkill = {
    humanId: string;
    label: string;
};

export type IndustrySkill = {
    humanId: string;
    label: string;
};

const defaultForm: ProfileCreationForm = {
    familiesCategoriesEdition: new FamiliesCategoriesEdition(),
    headline: null,
    experienceLevel: null,
    tags: [],
    industryExpertises: [],
    functionalSkills: [],
    price: 0,
    priceHide: false,
    address: defaultAddress,
    workPlacePreference: null,
    languages: [],
    phone: null,
    invitationCode: null,
    groupInvitationId: null,
};

export default new Vuex.Store({
    state: {
        form: defaultForm,
        familiesDefinition: [] as ProfileFamily[],
        qrcode: {
            url: '',
        },
    },
    mutations: {
        [mutations.loadFamiliesDefinition]: (state, families: ProfileFamily[]) => {
            state.familiesDefinition = families;
        },
        [mutations.selectManualFamily]: (state, family: string) => {
            state.form.familiesCategoriesEdition.manual.selectFamily(family);
        },
        [mutations.unselectPredictionFamilyAndCategory]: (state, familyAndCategory: {family: string; category: string}) => {
            state.form.familiesCategoriesEdition.predicted.unSelectCategory(
                familyAndCategory.family,
                familyAndCategory.category,
                state.familiesDefinition,
            );
        },
        [mutations.selectPredictionFamilyAndCategory]: (state, familyAndCategory: {family: string; category: string}) => {
            state.form.familiesCategoriesEdition.predicted.selectCategory(familyAndCategory.family, familyAndCategory.category);
        },
        [mutations.selectManualCategories]: (state, categories: Array<string>) => {
            state.form.familiesCategoriesEdition.manual.selectCategories(categories);
        },
        [mutations.unselectManualCategories]: (state) => {
            state.form.familiesCategoriesEdition.manual.resetSelection();
        },
        [mutations.selectManualSuggestedCategory]: (state, suggestedCategory: string) => {
            state.form.familiesCategoriesEdition.manual.selectOtherCategory(suggestedCategory);
        },
        [mutations.switchToCategoryPrediction]: (state) => {
            state.form.familiesCategoriesEdition.switchToPrediction();
        },
        [mutations.switchToCategoryManual]: (state) => {
            state.form.familiesCategoriesEdition.switchToManual();
        },
        [mutations.clearFamiliesAndCategories]: (state) => {
            state.form.familiesCategoriesEdition.resetAll();
        },
        [mutations.selectAddress]: (state, address: Address) => {
            state.form.address = address;
        },
        [mutations.selectExperienceLevel]: (state, experienceLevel: ExperienceLevel) => {
            state.form.experienceLevel = experienceLevel;
        },
        [mutations.unselectExperienceLevel]: (state) => {
            state.form.experienceLevel = null;
        },
        [mutations.selectPrice]: (state, price: number) => {
            state.form.price = price;
        },
        [mutations.selectPriceHide]: (state, hide: boolean) => {
            state.form.priceHide = hide;
        },
        [mutations.selectTag]: (state, tag: Tag) => {
            state.form.tags.push(tag);
        },
        [mutations.selectPhone]: (state, phone: string) => {
            state.form.phone = phone;
        },
        [mutations.selectWorkplacePreference]: (state, workPlacePreference: WorkplacePreference) => {
            state.form.workPlacePreference = workPlacePreference;
        },
        [mutations.unselectTag]: (state, tag: Tag) => {
            state.form.tags = state.form.tags.filter((t: Tag) => t.name !== tag.name);
        },
        [mutations.selectInvitationCode]: (state, invitationCode: string) => {
            state.form.invitationCode = invitationCode;
        },
        [mutations.selectGroupInvitationId]: (state, groupInvitationId: string) => {
            state.form.groupInvitationId = groupInvitationId;
        },
        [mutations.resetDetails]: (state) => {
            state.form.headline = null;
            state.form.address = defaultAddress;
            state.form.experienceLevel = null;
            state.form.price = 0;
            state.form.phone = null;
            state.form.tags = [];
        },
        [mutations.resetDetailsInPredictor]: (state) => {
            state.form.address = defaultAddress;
            state.form.price = 0;
            state.form.phone = null;
            state.form.tags = [];
        },
        [mutations.setupQrcode]: (state, qrcodeUrl: string) => {
            state.qrcode.url = qrcodeUrl;
        },
        [mutations.changeLanguages]: (state, languages: Language[]) => {
            state.form.languages = languages;
        },
        [mutations.updateIndustryExpertises]: (state, expertises: IndustrySkill[]) => {
            state.form.industryExpertises = expertises;
        },
        [mutations.updateFunctionalSkills]: (state, functionalSkills: FunctionalSkill[]) => {
            state.form.functionalSkills = functionalSkills;
        },
    },
    getters: {
        [getters.creationForm]: (state) => {
            return state.form;
        },
        [getters.qrcodeSetup]: (state) => {
            return state.qrcode;
        },
        [getters.selectedCategories]: (state) => {
            return state.form.familiesCategoriesEdition.allSelectedCategories();
        },
    },
});
